import {
  ArrowBack,
  TrendingUp,
  AttachMoneyOutlined,
  HistoryOutlined,
  CheckCircleOutlined,
  CalendarTodayOutlined,
  PhoneOutlined,
  EmailOutlined,
  PersonOutline,
  BusinessOutlined,
  BadgeOutlined,
  AccountBalanceOutlined,
  VerifiedOutlined,
  ArrowForward,
  PaymentsOutlined,
} from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import {
  Button,
  CardContainer,
  Tag,
  Tabs,
  DataTable,
  SearchBar,
  Dropdown,
} from 'finara-components';
import type { DataTableColumn, DropdownOption } from 'finara-components';
import { useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import { EditEmployeeModal } from '@/components/admin/employees/EditEmployeeModal';
import { EditUserInfoModal } from '@/components/admin/employees/EditUserInfoModal';
import { ErrorState, LoadingState, CsvExportButton } from '@/components/common';
import { useBankAccounts } from '@/hooks/queries/useAccounts';
import { useCreditsByUser } from '@/hooks/queries/useCredits';
import { employeeKeys, useEmployeeProfile } from '@/hooks/queries/useEmployees';
import { useCreateJobTitle, useUpdateJobTitle } from '@/hooks/queries/useJobTitles';
import { useCreateSalary, useUpdateSalary } from '@/hooks/queries/useSalaries';
import { useTransactions } from '@/hooks/queries/useTransactions';
import { userKeys, useUserDetails, useUpdateUser } from '@/hooks/queries/useUsers';
import type { EmployeeData } from '@/types/analytics';
import type { Credit } from '@/types/credits';
import type { DepositNavigationState } from '@/types/payment';
import type { Transaction } from '@/types/transactions';
import type { UpdateUserData } from '@/types/user';
import { formatNumber } from '@/utils/formatters';

interface Tab {
  id: string;
  title: string;
  onSelect: () => void;
}

interface EmployeeDetailContentProps {
  /** Employee (user) UUID. */
  employeeId: string | undefined;
  /**
   * Super admin only: scope all employee data to this organization. Must be
   * omitted in admin/manager context (the backend 403s them if sent).
   */
  organizationId?: string;
  /** Drives context-specific behavior (e.g. the advances CTA). */
  mode: 'admin' | 'super-admin';
  /** Back navigation handler, owned by the page wrapper. */
  onBack: () => void;
  /** Label for the back link. */
  backLabel: string;
}

export const EmployeeDetailContent: React.FC<EmployeeDetailContentProps> = ({
  employeeId,
  organizationId,
  mode,
  onBack,
  backLabel,
}) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('resumen');
  const [searchMovements, setSearchMovements] = useState('');
  const [movementTypeFilter, setMovementTypeFilter] = useState('');
  const [movementPeriodFilter, setMovementPeriodFilter] = useState('');
  const [isEditUserInfoModalOpen, setIsEditUserInfoModalOpen] = useState(false);
  const [isEditEmployeeModalOpen, setIsEditEmployeeModalOpen] = useState(false);
  const queryClient = useQueryClient();

  // Fetch data from backend
  const {
    data: profileData,
    isLoading: isLoadingProfile,
    error: profileError,
  } = useEmployeeProfile(employeeId, organizationId);

  const {
    data: userDetailsData,
    isLoading: _isLoadingUserDetails,
    error: _userDetailsError,
  } = useUserDetails(employeeId, organizationId);

  const {
    data: creditsData,
    isLoading: _isLoadingCredits,
    error: _creditsError,
  } = useCreditsByUser(employeeId, organizationId);

  const {
    data: transactionsData,
    isLoading: _isLoadingTransactions,
    error: _transactionsError,
  } = useTransactions({
    user_id: employeeId || '',
    organization_id: organizationId,
    limit: 50,
    offset: 0,
  });

  const { data: bankAccountsData } = useBankAccounts(employeeId, organizationId);

  // Mutations
  const updateUser = useUpdateUser();
  const createSalary = useCreateSalary();
  const updateSalary = useUpdateSalary();
  const createJobTitle = useCreateJobTitle();
  const updateJobTitle = useUpdateJobTitle();

  // Check if main data is loading or has error
  const isLoading = isLoadingProfile;
  const hasError = profileError;

  // Calculate derived data
  const employee = useMemo(() => {
    if (!profileData) return null;

    const { employee: emp, salary, financialMetrics, kyc } = profileData;
    const joinDate = new Date(emp.joinDate);
    const monthsInCompany = Math.floor(
      (Date.now() - joinDate.getTime()) / (1000 * 60 * 60 * 24 * 30)
    );

    return {
      id: emp.id,
      name: emp.name,
      email: emp.email,
      employeeId: emp.employeeCode,
      department: emp.department,
      position: profileData.jobTitle?.name,
      status: emp.status,
      isActive: emp.isActive,
      joinDate: emp.joinDate,
      currentBalance: financialMetrics.currentBalance,
      salary: salary?.monthlySalary,
      dailySalary: salary?.monthlySalary ? salary.monthlySalary / 30 : 0,
      hourlySalary: salary?.monthlySalary ? salary.monthlySalary / 30 / 8 : 0,
      historicalAdvances: financialMetrics.historicalAdvances,
      punctuality: financialMetrics.punctuality,
      averageMonthlyExpense: financialMetrics.monthlyExpenses30d,
      monthlyExpenses: financialMetrics.monthlyExpenses30d,
      monthsInCompany,
      kycStatus: kyc.status,
      lastActivity: profileData.lastActivity
        ? new Date(profileData.lastActivity.date).toLocaleDateString('es-MX')
        : null,
      antiquity: `${Math.floor(monthsInCompany / 12)} años ${monthsInCompany % 12} meses`,
      hasActiveAdvance: profileData.hasActiveAdvance,
    };
  }, [profileData]);

  // Handle loading state
  if (isLoading) {
    return <LoadingState message="Cargando perfil del empleado..." />;
  }

  // Handle error state
  if (hasError || !employee) {
    return <ErrorState title="Error al cargar el perfil" error={hasError} showRetry />;
  }

  // Handler for updating user info
  const handleUpdateUserInfo = async (data: {
    name?: string;
    rfc?: string;
    curp?: string;
    address?: string;
  }) => {
    if (!employeeId || updateUser.isPending) return;

    try {
      await updateUser.mutateAsync({ userId: employeeId, data });
      setIsEditUserInfoModalOpen(false);
      toast.success('Información actualizada exitosamente');
    } catch (error) {
      if (error instanceof Error && error.message.includes('already exists')) {
        toast.error('Este correo electrónico ya está en uso. Usa otro correo.');
      } else {
        const errorMessage =
          error instanceof Error ? error.message : 'Error al actualizar la información';
        toast.error(errorMessage);
      }
      console.error('Error al actualizar información del usuario:', error);
    }
  };

  // EditEmployeeModal expects an EmployeeData-shaped object; only id, name,
  // email, department (name) and isActive are read by the modal.
  const employeeForEdit: EmployeeData = {
    id: employee.id,
    name: employee.name,
    email: employee.email ?? '',
    department: employee.department ?? '',
    jobTitle: employee.position ?? '',
    status: employee.status,
    isActive: employee.isActive,
    kycCompleted: false,
    monthlySalary: employee.salary ?? 0,
    salaryCurrency: 'MXN',
    role: '',
    lastPayrollPayment: null,
    lastPayrollAmount: null,
    createdAt: employee.joinDate,
  };

  const employeeClabe = bankAccountsData?.[0]?.clabe;

  const handleDeposit = () => {
    if (!employeeClabe) return;
    const state: DepositNavigationState = {
      depositBeneficiary: { fullName: employee.name, rfc: '', clabe: employeeClabe },
    };
    void navigate('/pagos', { state });
  };

  // Handler for the richer employee edit (department, salary, job title, status)
  const handleEditEmployee = async (
    data: UpdateUserData,
    salaryData?: {
      salaryId?: string;
      amount: number;
      type: string;
      currency?: string;
      frequency?: string;
    },
    jobTitleData?: { jobTitleId?: string; name: string; description?: string }
  ) => {
    if (!employeeId) return;

    try {
      if (Object.keys(data).length > 0) {
        await updateUser.mutateAsync({ userId: employeeId, data });
      }

      if (salaryData) {
        if (salaryData.salaryId) {
          await updateSalary.mutateAsync({
            id: salaryData.salaryId,
            userId: employeeId,
            data: {
              amount: salaryData.amount,
              type: salaryData.type as 'base' | 'bonus' | 'commission' | 'overtime' | 'deduction',
            },
          });
        } else {
          await createSalary.mutateAsync({
            userId: employeeId,
            amount: salaryData.amount,
            currency: (salaryData.currency as 'MXN' | 'USD' | 'EUR' | undefined) ?? 'MXN',
            frequency: (salaryData.frequency as 'monthly' | 'biweekly' | undefined) ?? 'monthly',
            type: salaryData.type as 'base' | 'bonus' | 'commission' | 'overtime' | 'deduction',
          });
        }
      }

      if (jobTitleData) {
        if (jobTitleData.jobTitleId) {
          await updateJobTitle.mutateAsync({
            id: jobTitleData.jobTitleId,
            userId: employeeId,
            data: { name: jobTitleData.name, description: jobTitleData.description },
          });
        } else {
          await createJobTitle.mutateAsync({
            userId: employeeId,
            name: jobTitleData.name,
            description: jobTitleData.description,
          });
        }
      }

      // The header/resumen read from the employee profile, which useUpdateUser
      // does not invalidate; refresh the org-scoped profile + details explicitly.
      await queryClient.invalidateQueries({
        queryKey: employeeKeys.profile(employeeId, organizationId),
      });
      await queryClient.invalidateQueries({
        queryKey: userKeys.details(employeeId, organizationId),
      });

      setIsEditEmployeeModalOpen(false);
      toast.success('Empleado actualizado exitosamente');
    } catch (error) {
      if (error instanceof Error && error.message.includes('already exists')) {
        toast.error('Este correo electrónico ya está en uso. Usa otro correo.');
      } else {
        toast.error(
          error instanceof Error ? error.message : 'Error al actualizar el empleado'
        );
      }
      console.error('Error al actualizar empleado:', error);
    }
  };

  const tabs: Tab[] = [
    { id: 'resumen', title: 'Resumen', onSelect: () => setActiveTab('resumen') },
    {
      id: 'informacion',
      title: 'Información Personal',
      onSelect: () => setActiveTab('informacion'),
    },
    { id: 'movimientos', title: 'Movimientos', onSelect: () => setActiveTab('movimientos') },
    { id: 'adelantos', title: 'Adelantos', onSelect: () => setActiveTab('adelantos') },
  ];

  const movementTypeOptions: DropdownOption[] = [
    { value: '', label: 'Todos los tipos' },
    { value: 'Nómina', label: 'Nómina' },
    { value: 'Carga', label: 'Carga' },
  ];

  const movementPeriodOptions: DropdownOption[] = [
    { value: '', label: 'Últimos 30 días' },
    { value: '7', label: 'Últimos 7 días' },
    { value: '30', label: 'Últimos 30 días' },
    { value: '90', label: 'Últimos 90 días' },
  ];

  const renderResumen = () => {
    if (!profileData) return null;

    return (
      <div className="!grid grid-cols-1 lg:!grid-cols-2 gap-6">
        {/* Financial Status */}
        <CardContainer>
          <div className="pb-6">
            <div className="flex items-center gap-2 mb-6">
              <AttachMoneyOutlined className="text-brand-white" />
              <h3 className="text-lg font-semibold text-brand-white">Estado Financiero</h3>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between py-3 border-b border-border">
                <p className="text-sm text-muted">Balance Actual</p>
                <p className="text-lg font-bold text-green-600">
                  ${formatNumber(profileData.financialMetrics.currentBalance)}
                </p>
              </div>
              <div className="flex items-center justify-between py-3 border-b border-border">
                <p className="text-sm text-muted">Salario Base</p>
                <p className="text-lg font-bold text-brand-white">
                  ${formatNumber(profileData.salary?.monthlySalary || 0)}
                </p>
              </div>
              <div className="flex items-center justify-between py-3">
                <p className="text-sm text-muted">Adelanto Activo</p>
                <p className="text-lg font-bold text-brand-white">
                  {profileData.hasActiveAdvance ? 'Sí' : 'No'}
                </p>
              </div>
            </div>
          </div>
        </CardContainer>

        <CardContainer>
          <div className="pb-6">
            <div className="flex items-center gap-2 mb-6">
              <HistoryOutlined className="text-brand-white" />
              <h3 className="text-lg font-semibold text-brand-white">Últimos Movimientos</h3>
            </div>

            <div className="space-y-4">
              {transactionsData?.data && transactionsData.data.length > 0 ? (
                transactionsData.data.slice(0, 3).map(transaction => (
                  <div
                    key={transaction.id}
                    className="flex items-start justify-between py-4 border-b border-border last:border-0"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Tag variant="blue">{transaction.type}</Tag>
                        <Tag variant={transaction.status === 'approved' ? 'green' : 'yellow'}>
                          {transaction.status === 'approved' ? 'Completado' : transaction.status}
                        </Tag>
                      </div>
                      <p className="font-medium text-brand-white mb-1">{transaction.description}</p>
                      <p className="text-sm text-muted">
                        {new Date(transaction.createdAt).toLocaleDateString('es-MX')}
                      </p>
                    </div>
                    <div className="text-right ml-4">
                      <p
                        className={`text-xl font-semibold ${
                          transaction.side === 'in' ? 'text-green-600' : 'text-red-600'
                        }`}
                      >
                        {transaction.side === 'in' ? '+' : '-'}${formatNumber(transaction.amount)}
                      </p>
                      <p className="text-xs text-muted uppercase mt-1">{transaction.type}</p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-muted">
                  <HistoryOutlined className="text-muted mb-2" style={{ fontSize: 48 }} />
                  <p>No hay movimientos recientes</p>
                </div>
              )}
            </div>
          </div>
        </CardContainer>

        {/* Performance */}
        <CardContainer className="lg:col-span-2">
          <div className="pb-6">
            <div className="flex items-center gap-2 mb-6">
              <TrendingUp className="text-brand-white" />
              <h3 className="text-lg font-semibold text-brand-white">Performance y Métricas</h3>
            </div>

            <div className="grid grid-cols-2 md:!grid-cols-4 gap-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-green-600">
                  {profileData.financialMetrics.punctuality}%
                </p>
                <p className="text-sm text-muted mt-1">Puntualidad en Pagos</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-blue-600">
                  {profileData.financialMetrics.historicalAdvances}
                </p>
                <p className="text-sm text-muted mt-1">Adelantos Históricos</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-purple-600">
                  ${formatNumber(profileData.financialMetrics.monthlyExpenses30d / 1000)}K
                </p>
                <p className="text-sm text-muted mt-1">Gasto Mensual Promedio</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-orange-600">{employee.monthsInCompany}</p>
                <p className="text-sm text-muted mt-1">Meses en la Empresa</p>
              </div>
            </div>
          </div>
        </CardContainer>
      </div>
    );
  };

  const renderInformacionPersonal = () => {
    if (!userDetailsData || !profileData) return null;

    return (
      <div className="grid grid-cols-1 lg:!grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-6">
          {/* Personal Information */}
          <CardContainer>
            <div className="pb-6 border-b border-border">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <PersonOutline className="text-brand-white" />
                  <h3 className="text-base font-semibold text-brand-white">Información Personal</h3>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="white"
                    size="small"
                    onClick={() => setIsEditUserInfoModalOpen(true)}
                  >
                    Editar datos
                  </Button>
                  <Button
                    variant="black"
                    size="small"
                    onClick={() => setIsEditEmployeeModalOpen(true)}
                  >
                    Editar empleo
                  </Button>
                </div>
              </div>
            </div>
            <div className="pt-6">
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-muted mb-1">Nombre Completo</p>
                  <p className="text-base text-brand-white">{userDetailsData.name}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted mb-1">ID Empleado</p>
                  <p className="text-base text-brand-white">{profileData.employee.employeeCode}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted mb-1">RFC</p>
                  <div className="flex items-center gap-2">
                    <p className="text-base text-brand-white">{userDetailsData.rfc || 'N/A'}</p>
                    <button className="text-muted hover:text-muted">
                      <BadgeOutlined fontSize="small" />
                    </button>
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted mb-1">CURP</p>
                  <div className="flex items-center gap-2">
                    <p className="text-base text-brand-white">{userDetailsData.curp || 'N/A'}</p>
                    <button className="text-muted hover:text-muted">
                      <BadgeOutlined fontSize="small" />
                    </button>
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted mb-1">Dirección</p>
                  <p className="text-base text-brand-white">{userDetailsData.address || 'N/A'}</p>
                </div>
              </div>
            </div>
          </CardContainer>

          {/* Work Information */}
          <CardContainer>
            <div className="pb-6 border-b border-border">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BusinessOutlined className="text-brand-white" />
                  <h3 className="text-base font-semibold text-brand-white">Información Laboral</h3>
                </div>
              </div>
            </div>
            <div className="pt-6">
              <div className="grid grid-cols-2 gap-6 mb-6">
                {profileData.employee.department && (
                  <div>
                    <p className="text-sm font-medium text-muted mb-1">Departamento</p>
                    <p className="text-base text-brand-white">{profileData.employee.department}</p>
                  </div>
                )}
                {profileData.jobTitle && (
                  <div>
                    <p className="text-sm font-medium text-muted mb-1">Puesto</p>
                    <p className="text-base text-brand-white">{profileData.jobTitle.name}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm font-medium text-muted mb-1">Fecha de Ingreso</p>
                  <p className="text-base text-brand-white">
                    {new Date(profileData.employee.createdAt).toLocaleDateString('es-MX')}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted mb-1">Antigüedad</p>
                  <p className="text-base text-brand-white">{employee.antiquity}</p>
                </div>
              </div>

              <div className="border-t border-border pt-6">
                <h4 className="text-sm font-semibold text-brand-white mb-4">Estructura Salarial</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-black/5 dark:bg-white/5 rounded-lg">
                    <p className="text-xl font-bold text-brand-white">
                      ${formatNumber(profileData.salary?.monthlySalary || 0)}
                    </p>
                    <p className="text-xs text-muted mt-1">Mensual</p>
                  </div>
                  <div className="text-center p-4 bg-black/5 dark:bg-white/5 rounded-lg">
                    <p className="text-xl font-bold text-brand-white">
                      ${formatNumber(employee.dailySalary)}
                    </p>
                    <p className="text-xs text-muted mt-1">Diario</p>
                  </div>
                  <div className="text-center p-4 bg-black/5 dark:bg-white/5 rounded-lg">
                    <p className="text-xl font-bold text-brand-white">
                      ${formatNumber(employee.hourlySalary)}
                    </p>
                    <p className="text-xs text-muted mt-1">Por Hora</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContainer>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Contact Information */}
          <CardContainer>
            <div className="pb-6 border-b border-border">
              <div className="flex items-center gap-2">
                <PhoneOutlined className="text-brand-white" />
                <h3 className="text-base font-semibold text-brand-white">Información de Contacto</h3>
              </div>
            </div>
            <div className="pt-6">
              <div className="space-y-4 mb-6">
                <div className="flex items-center gap-2">
                  <EmailOutlined fontSize="small" className="text-muted" />
                  <div className="flex-1">
                    <p className="text-sm text-brand-white">{userDetailsData.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <PhoneOutlined fontSize="small" className="text-muted" />
                  <div className="flex-1">
                    <p className="text-sm text-brand-white">
                      {userDetailsData.countryCode} {userDetailsData.phoneNumber}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </CardContainer>

          {/* Banking Information */}
          <CardContainer>
            <div className="pb-6 border-b border-border">
              <div className="flex items-center gap-2">
                <AccountBalanceOutlined className="text-brand-white" />
                <h3 className="text-base font-semibold text-brand-white">Información Bancaria</h3>
              </div>
            </div>
            <div className="pt-6">
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-muted mb-1">Banco</p>
                  <p className="text-base text-brand-white">
                    {bankAccountsData?.[0] ? 'Información bancaria registrada' : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted mb-1">Número de Cuenta</p>
                  <div className="flex items-center gap-2">
                    <p className="text-base text-brand-white">
                      {bankAccountsData?.[0]?.clabe.substring(0, 10) || 'N/A'}
                    </p>
                    {bankAccountsData?.[0] && (
                      <button className="text-muted hover:text-muted">
                        <VerifiedOutlined fontSize="small" />
                      </button>
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted mb-1">CLABE Interbancaria</p>
                  <div className="flex items-center gap-2">
                    <p className="text-base text-brand-white">
                      {bankAccountsData?.[0]?.clabe || 'N/A'}
                    </p>
                    {bankAccountsData?.[0] && (
                      <button className="text-muted hover:text-muted">
                        <VerifiedOutlined fontSize="small" />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="border-t border-border mt-6 pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted mb-1">Estado KYC</p>
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm text-brand-white">Estado Actual</p>
                      <Tag variant={profileData.kyc.completed ? 'green' : 'yellow'}>
                        {profileData.kyc.status}
                      </Tag>
                    </div>
                    <p className="text-xs text-muted">
                      {profileData.kyc.completedAt
                        ? `Completado el ${new Date(profileData.kyc.completedAt).toLocaleDateString('es-MX')}`
                        : 'Pendiente'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </CardContainer>
        </div>
      </div>
    );
  };

  const movementsColumns: DataTableColumn<Transaction>[] = [
    {
      key: 'date',
      label: 'Fecha',
      render: (_, transaction) => (
        <span className="text-sm text-brand-white">
          {new Date(transaction.createdAt).toLocaleDateString('es-MX')}
        </span>
      ),
    },
    {
      key: 'type',
      label: 'Tipo',
      render: (_, transaction) => <Tag variant="blue">{transaction.type}</Tag>,
    },
    {
      key: 'concept',
      label: 'Concepto',
      render: (_, transaction) => (
        <span className="text-sm text-brand-white">{transaction.description}</span>
      ),
    },
    {
      key: 'side',
      label: 'Dirección',
      render: (_, transaction) => (
        <span className="text-sm text-muted">
          {transaction.side === 'in' ? 'Entrada' : 'Salida'}
        </span>
      ),
    },
    {
      key: 'amount',
      label: 'Monto',
      render: (_, transaction) => (
        <span
          className={`text-sm font-semibold ${transaction.side === 'in' ? 'text-green-600' : 'text-red-600'}`}
        >
          {transaction.side === 'in' ? '+' : '-'}${formatNumber(transaction.amount)}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Estado',
      render: (_, transaction) => (
        <Tag variant={transaction.status === 'approved' ? 'green' : 'yellow'}>
          {transaction.status === 'approved' ? 'Completado' : transaction.status}
        </Tag>
      ),
    },
    {
      key: 'reference',
      label: 'Referencia',
      render: (_, transaction) => (
        <span className="text-sm text-muted font-mono">{transaction.id.substring(0, 8)}</span>
      ),
    },
  ];

  const renderMovimientos = () => (
    <CardContainer>
      <div>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-brand-white">Historial de Movimientos</h3>
            <p className="text-sm text-muted mt-1">
              {transactionsData?.count || 0} transacciones en total
            </p>
          </div>
          <CsvExportButton
            data={transactionsData?.data || []}
            filename={`movimientos_${userDetailsData?.name.replace(/\s+/g, '_') || 'empleado'}`}
            columns={(tx: Transaction) => ({
              Fecha: new Date(tx.createdAt).toLocaleDateString('es-MX'),
              Tipo: tx.type,
              Concepto: tx.description || 'N/A',
              Monto: `$${tx.amount.toLocaleString('es-MX')}`,
              Estado: tx.status,
            })}
            variant="white"
            size="small"
            buttonText="Exportar"
          />
        </div>

        <div className="flex flex-col lg:!flex-row items-center gap-4 mb-6">
          <SearchBar
            value={searchMovements}
            onChange={setSearchMovements}
            placeholder="Buscar por concepto o referencia..."
            className="flex-1 w-full lg:!w-auto"
          />
          <Dropdown
            id="movement-type-filter"
            value={movementTypeFilter}
            onChange={value => setMovementTypeFilter(String(value))}
            options={movementTypeOptions}
            placeholder="Todos los tipos"
            className="w-full lg:!w-auto"
          />
          <Dropdown
            id="movement-period-filter"
            value={movementPeriodFilter}
            onChange={value => setMovementPeriodFilter(String(value))}
            options={movementPeriodOptions}
            placeholder="Últimos 30 días"
            className="w-full lg:!w-auto"
          />
        </div>

        <div className="overflow-scroll">
          {transactionsData?.data && transactionsData.data.length > 0 ? (
            <DataTable columns={movementsColumns} data={transactionsData.data} />
          ) : (
            <div className="text-center py-12">
              <HistoryOutlined className="text-muted mb-4" style={{ fontSize: 48 }} />
              <p className="text-muted">No hay movimientos registrados</p>
            </div>
          )}
        </div>
      </div>
    </CardContainer>
  );

  const advancesColumns: DataTableColumn<Credit>[] = [
    {
      key: 'id',
      label: 'ID',
      render: (_, credit) => (
        <span className="text-sm font-mono text-brand-white">{credit.id.substring(0, 8)}</span>
      ),
    },
    {
      key: 'requestDate',
      label: 'Fecha Solicitud',
      render: (_, credit) => (
        <span className="text-sm text-brand-white">
          {new Date(credit.createdAt).toLocaleDateString('es-MX')}
        </span>
      ),
    },
    {
      key: 'amount',
      label: 'Monto Solicitado',
      render: (_, credit) => (
        <span className="text-sm font-semibold text-brand-white">${formatNumber(credit.amount)}</span>
      ),
    },
    {
      key: 'totalAmount',
      label: 'Total con Interés',
      render: (_, credit) => (
        <span className="text-sm font-semibold text-brand-white">
          ${formatNumber(credit.amount + credit.interest_to_due_date)}
        </span>
      ),
    },
    {
      key: 'approvalDate',
      label: 'Fecha Aprobación',
      render: (_, credit) => (
        <span className="text-sm text-muted">
          {credit.approved_at ? new Date(credit.approved_at).toLocaleDateString('es-MX') : '-'}
        </span>
      ),
    },
    {
      key: 'dueDate',
      label: 'Fecha Vencimiento',
      render: (_, credit) => (
        <span className="text-sm text-muted">
          {credit.due_date ? new Date(credit.due_date).toLocaleDateString('es-MX') : '-'}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Estado',
      render: (_, credit) => (
        <Tag
          variant={
            credit.status === 'active' ? 'green' : credit.status === 'pending' ? 'yellow' : 'gray'
          }
        >
          {credit.status === 'active'
            ? 'Activo'
            : credit.status === 'pending'
              ? 'Pendiente'
              : credit.status === 'completed'
                ? 'Completado'
                : credit.status}
        </Tag>
      ),
    },
  ];

  const renderAdelantos = () => {
    if (!profileData) return null;

    const totalHistoricalAmount = creditsData?.data
      ? creditsData.data.reduce((sum, c) => sum + (c.amount + c.interest_to_due_date), 0)
      : 0;

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:!grid-cols-3 gap-6">
          <CardContainer>
            <div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted mb-1">Adelanto Activo</p>
                  <p className="text-2xl font-bold text-brand-white">
                    {profileData.hasActiveAdvance ? 'Sí' : 'No'}
                  </p>
                </div>
                <div className="rounded-lg flex items-center justify-center">
                  <TrendingUp className="text-muted" />
                </div>
              </div>
            </div>
          </CardContainer>
          <CardContainer>
            <div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted mb-1">Total Histórico</p>
                  <p className="text-2xl font-bold text-brand-white">
                    {profileData.financialMetrics.historicalAdvances}
                  </p>
                </div>
                <div className="rounded-lg flex items-center justify-center">
                  <HistoryOutlined className="text-blue-600" />
                </div>
              </div>
            </div>
          </CardContainer>
          <CardContainer>
            <div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted mb-1">Monto Total</p>
                  <p className="text-2xl font-bold text-brand-white">
                    ${formatNumber(totalHistoricalAmount)}
                  </p>
                </div>
                <div className="rounded-lg flex items-center justify-center">
                  <AttachMoneyOutlined className="text-purple-600" />
                </div>
              </div>
            </div>
          </CardContainer>
        </div>

        <CardContainer>
          <div>
            <h3 className="text-lg font-semibold text-brand-white mb-6">Historial de Adelantos</h3>
            <div className="overflow-scroll">
              {creditsData?.data && creditsData.data.length > 0 ? (
                <DataTable columns={advancesColumns} data={creditsData.data} />
              ) : (
                <div className="text-center py-12">
                  <AttachMoneyOutlined className="text-muted mb-4" style={{ fontSize: 48 }} />
                  <p className="text-muted">No hay adelantos registrados</p>
                </div>
              )}
            </div>

            {mode === 'admin' && (
              <div className="mt-6 p-4 bg-black/5 dark:bg-white/5 rounded-lg">
                <h4 className="text-sm font-semibold text-brand-white mb-2">
                  Nueva Solicitud de Adelanto
                </h4>
                <p className="text-sm text-muted mb-4">
                  Para procesar una nueva solicitud de adelanto para este empleado, vaya al módulo de{' '}
                  <strong>Adelantos de Nómina</strong> donde podrá revisar la solicitud para su
                  aprobación.
                </p>
                <Button
                  variant="white"
                  size="small"
                  startIcon={<ArrowForward />}
                  onClick={() => void navigate('/operaciones-empresariales/adelantos-de-nomina')}
                >
                  Ir a Adelantos de Nómina
                </Button>
              </div>
            )}
          </div>
        </CardContainer>
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'resumen':
        return renderResumen();
      case 'informacion':
        return renderInformacionPersonal();
      case 'movimientos':
        return renderMovimientos();
      case 'adelantos':
        return renderAdelantos();
      default:
        return renderResumen();
    }
  };

  // TypeScript guard: narrow profileData (employee is already guaranteed above)
  if (!profileData) {
    return null;
  }

  return (
    <div className="px-4 py-8">
      {/* Back Button */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-muted hover:text-brand-white mb-6"
      >
        <ArrowBack fontSize="small" />
        {backLabel}
      </button>

      {/* Employee Header */}
      <div className="mb-6">
        <div className="lg:flex items-start gap-6">
          <div className="w-20 h-20 bg-brand-blue/30 rounded-full flex items-center justify-center">
            <span className="text-2xl font-semibold text-brand-white">
              {employee.name
                .split(' ')
                .map(n => n[0])
                .join('')}
            </span>
          </div>
          <div className="flex-1 mb-4 lg:!mb-0">
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-bold text-brand-white">{employee.name}</h1>
              <Tag variant={employee.isActive ? 'green' : 'gray'}>
                {employee.isActive ? 'Activo' : 'Inactivo'}
              </Tag>
              {profileData.hasActiveAdvance && <Tag variant="yellow">Adelanto Activo</Tag>}
              <div className="ml-auto">
                <Button
                  variant="black"
                  size="small"
                  startIcon={<PaymentsOutlined fontSize="small" />}
                  onClick={handleDeposit}
                  disabled={!employeeClabe}
                >
                  Depositar
                </Button>
              </div>
            </div>
            <div className="flex items-center gap-4 text-sm text-muted mb-4">
              <span className="flex items-center gap-1">
                <BadgeOutlined fontSize="small" /> ID: {employee.employeeId}
              </span>
              <span className="flex items-center gap-1">
                <BusinessOutlined fontSize="small" /> {employee.department}
              </span>
              <span className="flex items-center gap-1">
                <CalendarTodayOutlined fontSize="small" /> Desde{' '}
                {new Date(profileData.employee.joinDate).toLocaleDateString('es-MX', {
                  month: 'long',
                  year: 'numeric',
                })}
              </span>
            </div>
            <div className="grid grid-cols-2 lg:!grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-muted">Puesto:</p>
                <p className="font-semibold text-brand-white">{employee.position}</p>
              </div>
              <div>
                <p className="text-sm text-muted">Salario Mensual:</p>
                <p className="font-semibold text-brand-white">
                  ${formatNumber(employee.salary || 0)}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted">Estado KYC:</p>
                <Tag variant={profileData.kyc.completed ? 'green' : 'yellow'}>
                  {profileData.kyc.status}
                </Tag>
              </div>
              <div>
                <p className="text-sm text-muted">Última Actividad:</p>
                <p className="font-semibold text-brand-white">
                  {profileData.lastActivity
                    ? new Date(profileData.lastActivity.date).toLocaleDateString('es-MX')
                    : 'N/A'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-1 md:!grid-cols-3 gap-6 mb-6">
        <CardContainer>
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted mb-1">Balance Actual</p>
                <p className="text-3xl font-bold text-brand-white">
                  ${formatNumber(employee.currentBalance)}
                </p>
              </div>
              <div className=" rounded-lg flex items-center justify-center">
                <AttachMoneyOutlined className="text-green-600" />
              </div>
            </div>
          </div>
        </CardContainer>
        <CardContainer>
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted mb-1">Salario Mensual</p>
                <p className="text-3xl font-bold text-brand-white">
                  ${formatNumber(employee.salary || 0)}
                </p>
              </div>
              <div className=" rounded-lg flex items-center justify-center">
                <TrendingUp className="text-blue-600" />
              </div>
            </div>
          </div>
        </CardContainer>
        <CardContainer>
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted mb-1">Adelantos Históricos</p>
                <p className="text-3xl font-bold text-brand-white">{employee.historicalAdvances}</p>
              </div>
              <div className=" rounded-lg flex items-center justify-center">
                <HistoryOutlined className="text-orange-600" />
              </div>
            </div>
          </div>
        </CardContainer>
        <CardContainer>
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted mb-1">Puntualidad</p>
                <p className="text-3xl font-bold text-brand-white">{employee.punctuality}%</p>
              </div>
              <div className=" rounded-lg flex items-center justify-center">
                <CheckCircleOutlined className="text-green-600" />
              </div>
            </div>
          </div>
        </CardContainer>
        <CardContainer>
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted mb-1">Último Pago</p>
                <p className="text-3xl font-bold text-brand-white">
                  {profileData.financialMetrics.lastPayment
                    ? new Date(profileData.financialMetrics.lastPayment.date).toLocaleDateString(
                        'es-MX',
                        { day: 'numeric', month: 'short' }
                      )
                    : 'N/A'}
                </p>
              </div>
              <div className=" rounded-lg flex items-center justify-center">
                <CalendarTodayOutlined className="text-blue-600" />
              </div>
            </div>
          </div>
        </CardContainer>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <Tabs tabs={tabs} activeTabId={activeTab} />
      </div>

      {/* Tab Content */}
      {renderTabContent()}

      {/* Edit User Info Modal (basic info: name / RFC / CURP / address) */}
      <EditUserInfoModal
        isOpen={isEditUserInfoModalOpen}
        onClose={() => setIsEditUserInfoModalOpen(false)}
        onSubmit={data => void handleUpdateUserInfo(data)}
        userDetails={userDetailsData}
        isLoading={updateUser.isPending}
      />

      {/* Edit Employee Modal (department, status, salary, job title) */}
      <EditEmployeeModal
        isOpen={isEditEmployeeModalOpen}
        onClose={() => setIsEditEmployeeModalOpen(false)}
        onSubmit={(userData, salaryData, jobTitleData) =>
          void handleEditEmployee(userData, salaryData, jobTitleData)
        }
        employee={employeeForEdit}
        organizationId={organizationId}
        isLoading={
          updateUser.isPending ||
          createSalary.isPending ||
          updateSalary.isPending ||
          createJobTitle.isPending ||
          updateJobTitle.isPending
        }
      />
    </div>
  );
};
